module.exports = {
  content: [
    './src/pages/**/*.{js,ts,jsx,tsx}',
    './src/components/**/*.{js,ts,jsx,tsx}',
    './src/core/**/*.{js,ts,jsx,tsx}',
  ],
  important: '#app',
  theme: {
    extend: {
      animation: {
        pulse: 'tw-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;',
      },
      backgroundImage: ({ theme }) => ({
        'red-white-gradient': `linear-gradient(180deg, ${theme('colors.orange.700')}16 0%, ${theme(
          'colors.white'
        )} 50%)`,
        'red-gradient': `linear-gradient(98.44deg, ${theme(
          'colors.orange.700'
        )} 30.7%, ${theme('colors.orange.600')} 91.63%)`,
      }),
      blur: {
        xs: '2px',
      },
      backdropBlur: {
        xs: '2px',
      },
      borderRadius: {
        '4xl': '3rem',
        concave: '100% 0 0 100% / 50% 0 0  50%;',
      },
      boxShadow: {
        '3xl': '0px 2px 6px rgba(0, 0, 0, 0.25)',
        '4xl': '0px 1px 10px rgba(0, 0, 0, 0.25)',
        'glow-red': '0px 0px 14px -4px rgba(195, 48, 0, 0.25)',
        deep: '0px 2px 2px #1a1a1a',
        red: '4px 4px #C33000',
      },
      flex: {
        2: '2 2 0%',
      },
      // Typography - Web: https://www.figma.com/design/lwBSJr3UhgDJ0j38GYlQRS/B2C---Design-System-Foundations?node-id=320-428&t=03wspullBz5u1OjM-0
      fontSize: {
        micro: ['0.625rem', { lineHeight: '0.905rem' }], // font size 10px line height 14.48px
        mini: ['0.75rem', { lineHeight: '1.08rem' }], // 12px 17.38px
        small: ['0.875rem', { lineHeight: '1.5rem' }], // B2: 14px 24px
        regular: ['1rem', { lineHeight: '1.44rem' }], // 16px 23.17px
        medium: ['1.125rem', { lineHeight: '1.62rem' }], // 18px 26.06px
        large: ['1.25rem', { lineHeight: '1.81rem' }], // 20px 28.96px
        title3: ['1.5rem', { lineHeight: '2.17rem' }], // 24px 34.75px
        title2: ['2rem', { lineHeight: '2.89rem' }], // 32px 46.34px
        title1: ['3rem', { lineHeight: '4.34rem' }], // 48px 69.5px
      },
      inset: {
        15: '15%',
        '1/5': '20%',
        30: '30%',
      },
      keyframes: {
        // re-define pulse keyframe with tw- prefix because tripla chatbot has the same 'pulse' keyframe with different animation
        'tw-pulse': {
          '0% 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
      },
      screens: {
        '3xs': { min: '320px' },
        '2xs': { min: '375px' },
        xs: { min: '425px' },
        '3xs-max': { max: '320px' },
        '2xs-max': { max: '375px' },
        'xs-max': { max: '425px' },
        'sm-max': { max: '639px' },
        'md-max': { max: '767px' },
        'lg-max': { max: '1023px' },
      },
      spacing: {
        15: '3.75rem',
        18: '4.5rem',
        22: '5.5rem',
        30: '7.5rem',
        72: '18rem',
        76: '19rem',
        84: '21rem',
        100: '25rem',
        112: '28rem',
      },
      transitionProperty: {
        visibility: 'visibility',
        height: 'height',
        'grid-template-rows': 'grid-template-rows',
      },
      zIndex: {
        100: 100,
      },
    },
    willChange: {
      filter: 'filter',
    },
    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      black: '#231815',
      white: '#FFFFFF',
      koiRed: '#BB3E03',
      sumiBlack: '#001219',
      scrollbarColor: '#BBBCBC',
      footerBgColor: '#2F2B2C',

      /**
       * ---------------------------------
       * Primary Colors
       * There are the main colors that make up the majority of the colors in the design system.
       * ---------------------------------
       */

      /**
       * Figma: Akane Red
       *
       * Used for error states and in “destructive” actions.
       */
      red: {
        50: '#FFF3F1',
        100: '#FFE3DF',
        200: '#FFCDC5',
        300: '#FFAA9D',
        400: '#FF7964',
        500: '#FF644C', // primary
        600: '#ED3215',
        700: '#C8260D',
        800: '#A5230F',
        900: '#882414',
        950: '#4B0E04',
      },

      /**
       * Figma: Grayscale
       */
      gray: {
        50: '#F5F6F6',
        100: '#E6E7E7',
        200: '#D0D1D1',
        300: '#AFB1B0',
        400: '#868A8A',
        500: '#6B6F6F',
        600: '#555757',
        700: '#4E504F',
        800: '#444646',
        900: '#3C3D3D',
        950: '#212222', // primary
      },

      /**
       * Figma: Tabist Orange
       *
       * it is the primary color of the Tabist brand.
       */
      orange: {
        50: '#FFF6EC',
        100: '#FFEBD2',
        200: '#FFD3A4',
        300: '#FFB36B',
        400: '#FF882F',
        500: '#FF6507',
        600: '#F94800',
        700: '#C33000', // primary
        800: '#A32909',
        900: '#83250B',
        950: '#470F03',
      },

      /**
       * Figma: Yofuke Green
       */
      teal: {
        50: '#F0FDFB',
        100: '#CBFCF7',
        200: '#97F8F0',
        300: '#5BEDE6',
        400: '#29D8D5',
        500: '#10BCBC',
        600: '#008099', // primary
        700: '#0D7478',
        800: '#0F5D60',
        900: '#124C4F',
        950: '#032C30',
      },

      /**
       * ---------------------------------
       * Secondary Colors
       * Along with primary colors, it is helpful to have a selection of secondary colors
       * to use in components such as error, warning, success, alerts etc.
       * Also it is significantly used for marketing products.
       * These secondary colors should be used sparingly or as accents,
       * while the primary colors should take precedence.
       * ---------------------------------
       */

      /**
       * Figma: Wakakusa Green
       *
       * Used for success states and in “affirmative” actions.
       */
      green: {
        50: '#F7FDE8',
        100: '#EBFACD',
        200: '#D8F5A1',
        300: '#BCED69',
        400: '#9DDE34', // primary
        500: '#82C51D',
        600: '#639D13',
        700: '#4C7813',
        800: '#3F5F15',
        900: '#365116',
        950: '#1A2C07',
      },

      /**
       * Figma: Yamabuki Yellow
       */
      yellow: {
        50: '#FFF9EB',
        100: '#FDEDC8',
        200: '#FAD98D',
        300: '#F7BA42', // primary
        400: '#F6A829',
        500: '#EF8611',
        600: '#D4620B',
        700: '#B0430D',
        800: '#8F3411',
        900: '#752C12',
        950: '#431405',
      },
      info: {
        text: '#004AD8',
        background: '#E4EEFF',
        variant: '#003CB1',
        'on-variant': '#EDF3FF',
      },
      disabled: {
        'on-disabled': '#B3B7C3',
      },
    },
  },
};
